import React from 'react';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import { motion } from 'motion/react';
import {
  AppBar,
  Box,
  Button,
  Container,
  Divider,
  IconButton,
  Menu,
  MenuItem,
  Stack,
  Toolbar,
  Tooltip,
  Typography,
  alpha,
  useTheme,
} from '@mui/material';
import MenuIcon from '@mui/icons-material/Menu';
import Brightness4Icon from '@mui/icons-material/Brightness4';
import Brightness7Icon from '@mui/icons-material/Brightness7';
import BuildCircleIcon from '@mui/icons-material/BuildCircle';
import { useAuth } from '../contexts/AuthContext';
import { useColorMode } from '../App';
import { UserRole } from '../types';
import { adminNavSections, isAdminRole, isNavActive } from './navigation';
import type { NavItem } from './navigation';
import { duration, ease, glory, grey } from '../theme/tokens';
import { useMotionSafe } from '../motion/useMotionSafe';

/** The shop home page ("/" or "/shop/<slug>"), whose hero is dark steel. */
const isGarageHome = (pathname: string) => pathname === '/' || /^\/shop\/[^/]+\/?$/.test(pathname);

const Navbar: React.FC = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const theme = useTheme();
  const colorMode = useColorMode();
  const location = useLocation();
  const { enabled: motionEnabled } = useMotionSafe();
  const [anchorElNav, setAnchorElNav] = React.useState<null | HTMLElement>(null);

  const handleOpenNavMenu = (event: React.MouseEvent<HTMLElement>) => setAnchorElNav(event.currentTarget);
  const handleCloseNavMenu = () => setAnchorElNav(null);
  const handleLogout = () => {
    logout();
    handleCloseNavMenu();
  };

  // Built once and shared by the desktop bar and the mobile menu, so the two
  // can never drift apart.
  const navItems: NavItem[] = React.useMemo(() => {
    if (!isAuthenticated) {
      return [
        { to: '/', label: 'Home' },
        { to: '/shops', label: 'Shops' },
        { to: '/book-appointment', label: 'Book appointment' },
      ];
    }

    // Super admins manage shops too — previously only ADMIN saw these links,
    // which left super admins with no navigation at all. Admin screens use the
    // sidebar; this is what an admin sees on a public page.
    if (isAdminRole(user?.role)) {
      return adminNavSections(user?.role).flatMap((section) => section.items);
    }

    if (user?.role === UserRole.MECHANIC) {
      return [{ to: '/mechanic/schedule', label: 'My schedule' }];
    }

    return [{ to: '/', label: 'Home' }];
  }, [isAuthenticated, user?.role]);

  const isActive = (to: string) => isNavActive(to, location.pathname);

  const isDark = theme.palette.mode === 'dark';
  // Over the home page's steel hero the bar turns steel too, in both colour
  // modes, so the page does not open on a light strip; its accent turns red.
  const onSteel = isGarageHome(location.pathname);
  const linkColor = onSteel ? grey[400] : 'text.secondary';
  const linkActiveColor = onSteel ? grey[50] : 'text.primary';
  const markColor = onSteel ? glory[400] : theme.palette.primary.main;

  return (
    <AppBar
      position="sticky"
      sx={{
        zIndex: (t) => t.zIndex.appBar,
        ...(onSteel && {
          backgroundColor: alpha(grey[950], 0.88),
          borderBottomColor: alpha(grey[0], 0.08),
          color: grey[100],
        }),
      }}
    >
      <Container maxWidth="xl">
        <Toolbar disableGutters sx={{ minHeight: { xs: 60, md: 68 }, gap: 1 }}>
          {/* Mobile menu trigger */}
          <Box sx={{ display: { xs: 'flex', md: 'none' } }}>
            <IconButton
              size="large"
              aria-label="Open navigation menu"
              aria-controls="menu-appbar"
              aria-haspopup="true"
              onClick={handleOpenNavMenu}
              color="inherit"
              edge="start"
            >
              <MenuIcon />
            </IconButton>
            <Menu
              id="menu-appbar"
              anchorEl={anchorElNav}
              anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
              transformOrigin={{ vertical: 'top', horizontal: 'left' }}
              keepMounted
              open={Boolean(anchorElNav)}
              onClose={handleCloseNavMenu}
              slotProps={{ paper: { sx: { mt: 1, minWidth: 220, borderRadius: 2 } } }}
              sx={{ display: { xs: 'block', md: 'none' } }}
            >
              {navItems.map((item) => (
                <MenuItem
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  onClick={handleCloseNavMenu}
                  selected={isActive(item.to)}
                >
                  {item.label}
                </MenuItem>
              ))}

              <Divider sx={{ my: 0.5 }} />

              <MenuItem
                onClick={() => {
                  colorMode.toggleColorMode();
                  handleCloseNavMenu();
                }}
              >
                {isDark ? 'Light mode' : 'Dark mode'}
              </MenuItem>

              {!isAuthenticated ? (
                <MenuItem component={RouterLink} to="/login" onClick={handleCloseNavMenu}>
                  Sign in
                </MenuItem>
              ) : (
                <MenuItem onClick={handleLogout}>Sign out</MenuItem>
              )}
            </Menu>
          </Box>

          {/* Brand */}
          <Box
            component={RouterLink}
            to="/"
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              textDecoration: 'none',
              color: 'inherit',
              mr: { md: 3 },
              flexGrow: { xs: 1, md: 0 },
              justifyContent: { xs: 'center', md: 'flex-start' },
            }}
          >
            <Box
              component={motionEnabled ? motion.div : 'div'}
              {...(motionEnabled
                ? { whileHover: { rotate: 90 }, transition: { duration: duration.slow, ease: ease.out } }
                : {})}
              sx={{ display: 'grid', placeItems: 'center', color: markColor }}
            >
              <BuildCircleIcon sx={{ fontSize: 30 }} />
            </Box>
            <Typography
              variant="h6"
              noWrap
              sx={{ fontWeight: 700, letterSpacing: '-0.02em', fontSize: '1.2rem' }}
            >
              MechaniX
            </Typography>
          </Box>

          {/* Desktop navigation */}
          <Stack direction="row" spacing={0.5} sx={{ flexGrow: 1, display: { xs: 'none', md: 'flex' } }}>
            {navItems.map((item) => {
              const active = isActive(item.to);
              return (
                <Button
                  key={item.to}
                  component={RouterLink}
                  to={item.to}
                  disableRipple
                  sx={{
                    position: 'relative',
                    px: 1.75,
                    py: 1,
                    fontWeight: active ? 650 : 550,
                    color: active ? linkActiveColor : linkColor,
                    '&:hover': {
                      color: linkActiveColor,
                      backgroundColor: (t) =>
                        onSteel ? alpha(grey[50], 0.06) : alpha(t.palette.text.primary, isDark ? 0.06 : 0.04),
                    },
                  }}
                >
                  {item.label}
                  {/* Shared layoutId lets the underline slide between items */}
                  {active && motionEnabled && (
                    <motion.span
                      layoutId="nav-underline"
                      transition={{ duration: duration.base, ease: ease.out }}
                      style={{
                        position: 'absolute',
                        left: 12,
                        right: 12,
                        bottom: 4,
                        height: 2,
                        borderRadius: 2,
                        background: markColor,
                      }}
                    />
                  )}
                  {active && !motionEnabled && (
                    <Box
                      sx={{
                        position: 'absolute',
                        left: 12,
                        right: 12,
                        bottom: 4,
                        height: 2,
                        borderRadius: 2,
                        backgroundColor: markColor,
                      }}
                    />
                  )}
                </Button>
              );
            })}
          </Stack>

          {/* Actions */}
          <Stack direction="row" spacing={1} alignItems="center" sx={{ flexShrink: 0 }}>
            <Tooltip title={isDark ? 'Light mode' : 'Dark mode'}>
              <IconButton
                onClick={colorMode.toggleColorMode}
                color="inherit"
                aria-label="Toggle dark and light mode"
                size="small"
                sx={{ width: 38, height: 38 }}
              >
                {isDark ? <Brightness7Icon fontSize="small" /> : <Brightness4Icon fontSize="small" />}
              </IconButton>
            </Tooltip>

            <Box sx={{ display: { xs: 'none', md: 'flex' }, gap: 1 }}>
              {!isAuthenticated ? (
                <>
                  <Button component={RouterLink} to="/login" size="small" sx={{ color: linkColor }}>
                    Sign in
                  </Button>
                  <Button
                    component={RouterLink}
                    to="/book-appointment"
                    variant="contained"
                    size="small"
                    sx={
                      onSteel
                        ? { backgroundColor: glory[600], '&:hover': { backgroundColor: glory[700] } }
                        : undefined
                    }
                  >
                    Book now
                  </Button>
                </>
              ) : (
                <Button
                  onClick={handleLogout}
                  size="small"
                  variant="outlined"
                  sx={onSteel ? { color: grey[100], borderColor: alpha(grey[100], 0.4) } : undefined}
                >
                  Sign out
                </Button>
              )}
            </Box>
          </Stack>
        </Toolbar>
      </Container>
    </AppBar>
  );
};

export default Navbar;
